/**
 * BrainDumpPage — full-screen overlay with tab navigation.
 *
 * A13-N10 (capture) + N11 (detail) + N12 (timeline + search).
 * Matches settingsView pattern (WalletPage, ProfilePage, etc.)
 *
 * Props:
 *  - onClose: () => void
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import VoiceRecorder from './VoiceRecorder';
import PhotoCapture from './PhotoCapture';
import RecentCaptures from './RecentCaptures';
import BrainDumpNoteDetail from './BrainDumpNoteDetail';
import BrainDumpTimeline from './BrainDumpTimeline';
import BrainDumpSearch from './BrainDumpSearch';
import BrainDumpGraph from './BrainDumpGraph';
import BrainDumpTree from './BrainDumpTree';
import { useBrainDumpCapture, useRecentCaptures } from '../../hooks/useBrainDumpCapture';

const TABS = [
  { key: 'capture', labelKey: 'brainDump.tabCapture', fallback: 'Erfassen' },
  { key: 'timeline', labelKey: 'brainDump.tabTimeline', fallback: 'Timeline' },
  { key: 'search', labelKey: 'brainDump.tabSearch', fallback: 'Suche' },
  { key: 'graph', labelKey: 'brainDump.tabGraph', fallback: 'Graph' },
  { key: 'tree', labelKey: 'brainDump.tabTree', fallback: 'Ordner' },
];

export default function BrainDumpPage({ onClose }) {
  const { t } = useTranslation();
  const { captureText, captureVoice, capturePhoto, isCapturing, lastError } = useBrainDumpCapture();
  const { notes, loading, refetch } = useRecentCaptures(5);

  const [textInput, setTextInput] = useState('');
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [activeTab, setActiveTab] = useState('capture');

  // Fetch on mount
  useEffect(() => { refetch(); }, [refetch]);

  async function handleTextSubmit(e) {
    e.preventDefault();
    const text = textInput.trim();
    if (!text) return;

    try {
      await captureText(text);
      setTextInput('');
      await refetch();
    } catch {
      // Error shown via lastError
    }
  }

  async function handleVoiceCapture(blob, durationSeconds) {
    await captureVoice(blob, durationSeconds);
    await refetch();
  }

  async function handlePhotoCapture(file) {
    await capturePhoto(file);
    await refetch();
  }

  function handleNoteSelect(noteId) {
    setSelectedNoteId(noteId);
  }

  // Show note detail overlay
  if (selectedNoteId) {
    return (
      <BrainDumpNoteDetail
        noteId={selectedNoteId}
        onClose={() => { setSelectedNoteId(null); refetch(); }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight"
      data-testid="brain-dump-page"
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
          data-testid="brain-dump-back"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
          {t('brainDump.title', 'Notizen')}
        </span>
      </header>

      {/* Tab navigation */}
      <div className="flex border-b border-[var(--aif-border-default)] px-4 flex-shrink-0" data-testid="brain-dump-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-[var(--aif-color-primary)] text-[var(--aif-color-primary)]'
                : 'border-transparent text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)]'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {t(tab.labelKey, tab.fallback)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6">
          {/* Capture tab */}
          {activeTab === 'capture' && (
            <div className="space-y-6">
              {/* Quick text input */}
              <form onSubmit={handleTextSubmit}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder={t('brainDump.quickNote', 'Schnellnotiz...')}
                    disabled={isCapturing}
                    className="flex-1 px-4 py-3 border border-[var(--aif-border-default)] rounded-lg text-base bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] placeholder:text-[var(--aif-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--aif-color-primary)]"
                    data-testid="text-input"
                  />
                  <button
                    type="submit"
                    disabled={isCapturing || !textInput.trim()}
                    className="px-4 py-3 bg-[var(--aif-color-primary)] text-white rounded-lg font-medium disabled:opacity-50 hover:opacity-90 transition"
                    data-testid="text-submit"
                  >
                    ✍️
                  </button>
                </div>
              </form>

              {/* Big buttons: voice + photo */}
              <div className="grid grid-cols-2 gap-4 py-6">
                <div className="flex justify-center">
                  <VoiceRecorder onCapture={handleVoiceCapture} disabled={isCapturing} />
                </div>
                <div className="flex justify-center">
                  <PhotoCapture onCapture={handlePhotoCapture} disabled={isCapturing} />
                </div>
              </div>

              {lastError && (
                <div className="bg-[var(--aif-color-danger-light)] border border-[var(--aif-color-danger-border)] text-[var(--aif-color-danger)] px-3 py-2 rounded text-sm">
                  {lastError}
                </div>
              )}

              {/* Recent captures */}
              <div>
                <h2 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase mb-2 tracking-wide">
                  {t('brainDump.recentCaptures', 'Zuletzt erfasst')}
                </h2>
                <RecentCaptures notes={notes} loading={loading} onNoteSelect={handleNoteSelect} />
              </div>
            </div>
          )}

          {/* Timeline tab */}
          {activeTab === 'timeline' && (
            <BrainDumpTimeline onNoteSelect={handleNoteSelect} />
          )}

          {/* Search tab */}
          {activeTab === 'search' && (
            <BrainDumpSearch onNoteSelect={handleNoteSelect} />
          )}

          {/* Graph tab */}
          {activeTab === 'graph' && (
            <BrainDumpGraph onNoteSelect={handleNoteSelect} />
          )}

          {/* Tree tab */}
          {activeTab === 'tree' && (
            <BrainDumpTree onNoteSelect={handleNoteSelect} />
          )}
        </div>
      </div>
    </div>
  );
}
